import {Component, DestroyRef, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {Subject, takeUntil} from 'rxjs';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CurrentCrudSelectBehaviorServiceAsString, SnackBarService} from '../objectPassingService';
import {Router} from '@angular/router';
import {MatButton} from '@angular/material/button';
import {MatError, MatFormField, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {CreateCampaignGroupSequenceInitPayload} from '../campaign-group-sequence/campaign-group-sequence.component';
import {GetAllCampaignGroupSequencePayloadModel} from '../campaign-group-sequence/campaign_group_sequence_interfaces';
import {CampaignGroupSequenceService} from '../campaign-group-sequence/campaign-group-sequence.service';
import {environment} from '../../environments/environment';
import {CreateAdgroupInitPayload} from '../adgroups/adgroups.component';
import {GetAllCampaignGroupPayloadModel} from '../campaign-groups/campaign_groups_interfaces';
import {MatCard, MatCardActions, MatCardContent, MatCardHeader} from '@angular/material/card';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatDivider} from '@angular/material/divider';
import {MatList, MatListItem} from '@angular/material/list';
import {MatSelectModule} from '@angular/material/select';
import {MatFormFieldModule} from '@angular/material/form-field';
import {TooltipPosition, MatTooltipModule} from '@angular/material/tooltip';


function isCreateCampaignGroupSequenceInitPayload(obj: any): obj is CreateCampaignGroupSequenceInitPayload {
  return (
    obj &&
    typeof obj === 'object' &&
    obj.is_edit === false &&
    Object.keys(obj).length === 1
  );
}

function isGetAllCampaignGroupSequencePayloadModel(obj: any): obj is GetAllCampaignGroupSequencePayloadModel {
  /*
  The reason we kept both isCreateAdInitPayload() and isGetAllAdPayloadModel() was:
	•	To future-proof (in case is_edit accidentally shows up in GetAllAdPayloadModel)
	•	To keep the checks isolated and testable
	•	To avoid assumptions in the “else” branch
   */
  return obj && typeof obj === 'object' && typeof obj.name === 'string';
}



@Component({
  selector: 'app-campaign-group-sequence-meta',
  imports: [
    MatButton,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatProgressBar,
    ReactiveFormsModule,
    MatSlideToggle,
    MatCard,
    MatCardContent,
    MatCardActions,
    MatCardHeader,
    MatCheckbox,
    MatDivider,
    MatListItem,
    MatList,
    MatSelectModule,
    MatFormFieldModule,
    MatTooltipModule
  ],
  standalone: true,
  templateUrl: './campaign-group-sequence-meta.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campaign-group-sequence-meta.component.css'
})
export class CampaignGroupSequenceMetaComponent implements OnInit, OnDestroy{


  private destroyRef = inject(DestroyRef);
  destroyed = new Subject<void>();

  is_edit = false;
  is_form_error: boolean = false;
  form_error_message: string = "";
  myForm!: FormGroup;
  title_text = "Create Adgroup";
  submit_text = "Create"

  campaign_group_sequence_list: GetAllCampaignGroupSequencePayloadModel[] | undefined


  client_id: number = 0
  client_slug: string = '';
  campaign_group_sequence_id: string = '';
  is_loading = true;

  daysOptions = [0, 1, 2, 3, 4, 5];
  hoursOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
  maxRecurrence = [0, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  controlRateOptions = [{
    value: 0,
    viewValue: '0% (No Control)'
  }, {
    value: 0.1,
    viewValue: '10%'
  }, {
    value: 0.2,
    viewValue: '20%'
  }, {
    value: 0.3,
    viewValue: '30%'
  }, {
    value: 0.4,
    viewValue: '40%'
  }, {
    value: 0.5,
    viewValue: '50%'
  }
  ];

  campaign_group_sequence_select_list = [
    {'id': "root", 'name': 'Root'},
  ]

  constructor(
    private fb: FormBuilder,
    private snackBarService: SnackBarService,
    public router: Router, //all components need this
    private currentCrudSelectBehaviorService: CurrentCrudSelectBehaviorServiceAsString,
    private campaignGroupSequenceService: CampaignGroupSequenceService,
  ) {
  } // end constructor


  ngOnInit() {

    this.client_id = parseInt(localStorage.getItem('client_id') || '0');
    this.client_slug = localStorage.getItem('client_slug') || '';
    this.campaign_group_sequence_id = localStorage.getItem('campaign_group_sequence_id') || '';


    // Use localStorage data on reload
    const savedCampaignGroupMeta = localStorage.getItem('campaigngroupsequencesaved');

    const campaign_group_sequence_list_temp = localStorage.getItem('campaign_group_sequence_list');
    if ( campaign_group_sequence_list_temp) {
      this.campaign_group_sequence_list = JSON.parse(campaign_group_sequence_list_temp);

      if (this.campaign_group_sequence_list){
        for (const obj of this.campaign_group_sequence_list) {
          this.campaign_group_sequence_select_list.push({ id: obj.campaign_group_sequence_id, name: obj.name });
        }
      }

    }



    if (savedCampaignGroupMeta) {
      // now if I want I can ... if exists go back to ads rather than having
      // to deal with updating saved storage e.g. on input update saved storage
      // will keep on for development for now
      //this.backToAds()

      if (environment.environment == 'production') {
        this.backToCampaignGroupSequence()
      }

      try {
        const parsed = JSON.parse(savedCampaignGroupMeta);
        if (isCreateCampaignGroupSequenceInitPayload(parsed)) {
          const obj: CreateCampaignGroupSequenceInitPayload = parsed;
          console.log('[Reload] Editing state:', obj);
          this.setForm(); // Or pass in data if needed
          this.is_edit = obj.is_edit;
        } else if (isGetAllCampaignGroupSequencePayloadModel(parsed)) {
          const obj: GetAllCampaignGroupSequencePayloadModel = parsed;
          console.log('[Reload] Ad Data:', obj);
          this.setForm(obj);
          this.is_edit = true;
          this.campaign_group_sequence_id = obj.campaign_group_sequence_id
        }
      } catch (e) {
        console.error('Error parsing localStorage campaigngroupsequencesaved:', e);
      }
    } // end if savedAdMeta

    // Subscriptions from services (live interaction)
    this.currentCrudSelectBehaviorService.current$.pipe(takeUntil(this.destroyed)).subscribe(x => {
      if (!x) return;

      try {
        const parsed = JSON.parse(x);

        if (isCreateCampaignGroupSequenceInitPayload(parsed)) {
          console.log('[Stream] Create Payload:', parsed);
          this.setForm();
          this.is_edit = parsed.is_edit;
        } else if (isGetAllCampaignGroupSequencePayloadModel(parsed)) {
          console.log('[Stream] Edit Payload:', parsed);
          this.setForm(parsed);
          this.is_edit = true;
        } else {
          throw new Error('Unknown payload structure');
        }

        localStorage.setItem('campaigngroupsequencesaved', JSON.stringify(parsed));
      } catch (e) {
        console.error('Error parsing stream payload:', e);
      }
    }); // end subscription

  } // end ngOnInit

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  } // end ngOnDestroy

  setForm(data?: GetAllCampaignGroupSequencePayloadModel) {

    console.log(data);

    if (!data) {

      this.myForm = new FormGroup({
        name: new FormControl('', [Validators.required]),
        alias: new FormControl('', [Validators.required]),
        is_recurrent: new FormControl(false, [Validators.required]),
        self_service_sms: new FormControl(false, [Validators.required]),
        is_pre_scheduled: new FormControl(false, [Validators.required]),
        status: new FormControl(false, [Validators.required]),
        campaign_staleness_threshold_in_days: new FormControl('', [Validators.required]),
        adjust_weekend_stale: new FormControl(false, [Validators.required]),
        recurrent_cooldown_in_days: new FormControl('', [Validators.required]),
        max_recurrence_count: new FormControl('', [Validators.required]),  // 1 == no recurrence   0 == unlimited
        campaign_group_sequence_parent_id: new FormControl(''),
        control_rate: new FormControl('', [Validators.required]),
        terminate_if_blown: new FormControl(false, [Validators.required]),
      });

    } else {
      this.submit_text = "Edit"

      let is_status =false;
      if (data.status == "active") {
        is_status = true;
      } else {
        is_status = false;
      }


      this.myForm = this.fb.group({
        name: [data.name],
        alias: [data.alias],
        status: [is_status],
        is_recurrent: [data.is_recurrent],
        self_service_sms: [data.self_service_sms],
        is_pre_scheduled: [data.is_pre_scheduled],
        campaign_staleness_threshold_in_days: [data.campaign_staleness_threshold_in_days],
        recurrent_cooldown_in_days: [data.recurrent_cooldown_in_days],
        adjust_weekend_stale: [data.adjust_weekend_stale],
        max_recurrence_count: [data.max_recurrence_count],
        campaign_group_sequence_parent_id: [data.campaign_group_sequence_parent_id || 'root'],
        control_rate: [data.control_rate],
        terminate_if_blown: [data.terminate_if_blown]
      });
    }

    this.is_loading = false
  } // setForm

  backToCampaignGroupSequence() {
    localStorage.removeItem('campaigngroupsequencesaved')
    //this.router.navigate(['/nav-campaigns', {outlets: {admeta: ['ads']}}]);
    this.router.navigate(['/nav-campaigns',  { outlets: { admeta: ['campaign-group-sequence'] } }]).then(r => console.log('redirect to campaign-groups'));
  } // end backCampaignGroups

  onFocus(){
    console.log('onFocus')
    this.is_form_error = false;
    this.form_error_message = "";
  } // end onFocus

  onSubmit(){

    this.is_loading = true;
    let form_data = this.myForm.value;
    form_data['client_id'] =  this.client_id  //Math.floor(parseInt(form_data['client_id'], 10));

    let status = 'paused';
    if (form_data['status'] == true) {
      status = 'active';
    } else {
      status = 'paused';
    }
    form_data['status'] = status;

    if (!this.is_edit) {

      this.campaignGroupSequenceService.post(`adserver/campaign_group_sequence/create/${this.client_id}`, form_data).pipe(takeUntil(this.destroyed)).subscribe(x => {
        this.snackBarService.announceSackBar('Campaign Group Sequence Created');
        this.is_loading = false;
        this.backToCampaignGroupSequence();
      }, error => {
        console.log('error',error)

      });

    } else {

      let endpoint = `adserver/campaign_group_sequence/${this.campaign_group_sequence_id}`;
      this.campaignGroupSequenceService.put(endpoint, form_data).subscribe(x => {
        this.snackBarService.announceSackBar('Campaign Group Sequence Updated');
        this.is_loading = false;
      }, error => {
        this.is_loading = false;
        this.snackBarService.announceSackBar('Campaign Group Sequence Update Error');
        console.log('error',error)

      });


    } //end if

  } // end OnSubmit
















} // end of CampaignGroupSequenceMetaComponent
